import type {
	ConnectionQuality,
	LocalTrackPublication,
	RemoteTrack,
	RemoteTrackPublication,
	Room
} from 'livekit-client';
import type { CallJoinCredentials, CallKind } from '@andresot/protocol';

export type CameraFacingMode = 'user' | 'environment';
/** Что показывать у участника: его камеру или экран, которым он делится. */
export type CallVideoSource = 'camera' | 'screen';
export interface InitialCallMediaOptions {
	microphoneEnabled: boolean;
	cameraEnabled: boolean;
	cameraFacingMode?: CameraFacingMode;
}
type VideoPublication = LocalTrackPublication | RemoteTrackPublication;
type MediaConnectionQuality = 'excellent' | 'good' | 'poor' | 'lost' | 'unknown';

function mediaError(kind: 'camera' | 'microphone', problem: unknown): string {
	const name = problem instanceof DOMException || problem instanceof Error ? problem.name : '';
	const device = kind === 'camera' ? 'камере' : 'микрофону';
	const title = kind === 'camera' ? 'Камера' : 'Микрофон';
	if (name === 'NotAllowedError' || name === 'SecurityError')
		return `Доступ к ${device} запрещён. Разрешите его в настройках браузера и попробуйте снова.`;
	if (name === 'NotFoundError' || name === 'DevicesNotFoundError')
		return kind === 'camera' ? 'Камера не найдена.' : 'Микрофон не найден.';
	if (name === 'NotReadableError' || name === 'TrackStartError')
		return `${title} занят другим приложением или недоступен.`;
	if (name === 'OverconstrainedError' || name === 'ConstraintNotSatisfiedError')
		return `Выбранный ${kind === 'camera' ? 'режим камеры' : 'микрофон'} недоступен.`;
	return `Не удалось включить ${kind === 'camera' ? 'камеру' : 'микрофон'}. Проверьте устройство и разрешения браузера.`;
}

class CallMediaState {
	connectedCallId = $state<string | null>(null);
	connectionState = $state('disconnected');
	error = $state<string | null>(null);
	cameraError = $state<string | null>(null);
	microphoneError = $state<string | null>(null);
	audioPlaybackBlocked = $state(false);
	revision = $state(0);
	devices = $state<MediaDeviceInfo[]>([]);
	speakingIds = $state<string[]>([]);
	levels = $state<Record<string, number>>({});
	connectionQualities = $state<Record<string, MediaConnectionQuality>>({});
	activeAudioInputDeviceId = $state<string | null>(null);
	activeVideoDeviceId = $state<string | null>(null);
	activeAudioOutputDeviceId = $state<string | null>(null);
	preferredCameraFacingMode = $state<CameraFacingMode>('user');
	deviceSwitchingKind = $state<MediaDeviceKind | null>(null);
	ready = $state(false);
	microphoneEnabled = $state(false);
	cameraEnabled = $state(false);
	screenShareEnabled = $state(false);
	speakerSelectionSupported = $state(
		typeof HTMLMediaElement !== 'undefined' && 'setSinkId' in HTMLMediaElement.prototype
	);
	screenShareSupported = $state(
		typeof window !== 'undefined' &&
			window.isSecureContext &&
			typeof navigator.mediaDevices?.getDisplayMedia === 'function'
	);

	private room: Room | null = null;
	private e2eeWorker: Worker | null = null;
	private recordingMode = false;
	private audioElements = new Map<string, HTMLMediaElement[]>();
	private levelTimer: ReturnType<typeof setInterval> | null = null;
	private microphoneRequested = false;
	private cameraRequested = false;
	private microphoneRecovery: Promise<void> | null = null;
	private cameraRecovery: Promise<void> | null = null;

	connectionQualityFor(userId: string): 0 | 1 | 2 | 3 {
		switch (this.connectionQualities[userId]) {
			case 'excellent':
				return 3;
			case 'good':
				return 2;
			case 'poor':
				return 1;
			default:
				return 0;
		}
	}

	connectionQualityLabelFor(userId: string): string {
		switch (this.connectionQualities[userId]) {
			case 'excellent':
				return 'Отличное качество связи';
			case 'good':
				return 'Хорошее качество связи';
			case 'poor':
				return 'Слабая связь';
			case 'lost':
				return 'Связь потеряна';
			default:
				return 'Качество связи определяется';
		}
	}

	get hasWeakConnection(): boolean {
		return Object.values(this.connectionQualities).some(
			(quality) => quality === 'poor' || quality === 'lost'
		);
	}

	async connect(
		credentials: CallJoinCredentials,
		kind: CallKind,
		initial: InitialCallMediaOptions = {
			microphoneEnabled: true,
			cameraEnabled: kind === 'video',
			cameraFacingMode: 'user'
		}
	): Promise<void> {
		if (this.connectedCallId === credentials.call.id && this.room) return;
		await this.disconnect();
		const livekit = await import('livekit-client');
		const keyProvider = new livekit.ExternalE2EEKeyProvider();
		await keyProvider.setKey(credentials.e2eeKey);
		const e2eeWorker = new Worker(new URL('../workers/livekit-e2ee.worker.ts', import.meta.url), {
			type: 'module'
		});
		const room = new livekit.Room({
			adaptiveStream: true,
			dynacast: true,
			publishDefaults: {
				audioPreset: livekit.AudioPresets.speech,
				dtx: true,
				red: true,
				simulcast: true
			},
			e2ee: { keyProvider, worker: e2eeWorker }
		});
		this.room = room;
		this.e2eeWorker = e2eeWorker;
		this.connectedCallId = credentials.call.id;
		this.preferredCameraFacingMode = initial.cameraFacingMode ?? this.preferredCameraFacingMode;
		this.microphoneRequested = initial.microphoneEnabled;
		this.cameraRequested = kind === 'video' && initial.cameraEnabled;
		this.error = this.cameraError = this.microphoneError = null;
		this.audioPlaybackBlocked = false;
		this.ready = false;
		this.bindRoom(room, livekit);

		try {
			await room.connect(credentials.url, credentials.token);
			this.setConnectionQuality(
				room.localParticipant.identity,
				room.localParticipant.connectionQuality
			);
			for (const participant of room.remoteParticipants.values())
				this.setConnectionQuality(participant.identity, participant.connectionQuality);
			this.recordingMode = credentials.call.recording;
			if (!this.recordingMode) await room.setE2EEEnabled(true);
			this.connectionState = room.state;
			if (initial.microphoneEnabled) {
				try {
					await room.localParticipant.setMicrophoneEnabled(true);
					this.microphoneError = null;
				} catch (problem) {
					this.microphoneError = mediaError('microphone', problem);
				}
			}
			if (kind === 'video' && initial.cameraEnabled) {
				try {
					await room.localParticipant.setCameraEnabled(true, {
						facingMode: this.preferredCameraFacingMode
					});
					this.cameraError = null;
				} catch (problem) {
					this.cameraError = mediaError('camera', problem);
				}
			}
			await this.refreshDevices();
			this.syncLocalState(room);
			if (initial.microphoneEnabled && !this.microphoneEnabled && !this.microphoneError)
				this.microphoneError = 'Микрофон не запущен. Проверьте устройство и разрешения браузера.';
			if (kind === 'video' && initial.cameraEnabled && !this.cameraEnabled && !this.cameraError)
				this.cameraError = 'Камера не запущена. Проверьте устройство и разрешения браузера.';
			this.watchLevels(room);
			this.audioPlaybackBlocked = !room.canPlaybackAudio;
			this.ready = true;
		} catch (problem) {
			const message =
				problem instanceof Error ? problem.message : 'Не удалось подключиться к звонку';
			await this.disconnect();
			this.error = message;
			throw problem;
		}
	}

	async disconnect(): Promise<void> {
		const room = this.room;
		const e2eeWorker = this.e2eeWorker;
		this.room = null;
		this.e2eeWorker = null;
		this.connectedCallId = null;
		this.connectionState = 'disconnected';
		this.recordingMode = false;
		this.speakingIds = [];
		this.levels = {};
		this.connectionQualities = {};
		this.devices = [];
		this.activeAudioInputDeviceId =
			this.activeVideoDeviceId =
			this.activeAudioOutputDeviceId =
				null;
		this.deviceSwitchingKind = null;
		this.error = this.cameraError = this.microphoneError = null;
		this.audioPlaybackBlocked = false;
		if (this.levelTimer) clearInterval(this.levelTimer);
		this.levelTimer = null;
		this.ready = false;
		this.microphoneEnabled = false;
		this.cameraEnabled = false;
		this.screenShareEnabled = false;
		this.microphoneRequested = false;
		this.cameraRequested = false;
		this.microphoneRecovery = null;
		this.cameraRecovery = null;
		for (const elements of this.audioElements.values())
			for (const element of elements) element.remove();
		this.audioElements.clear();
		if (room) await room.disconnect();
		e2eeWorker?.terminate();
		this.revision += 1;
	}

	async setRecordingMode(recording: boolean): Promise<void> {
		if (!this.room || this.recordingMode === recording) return;
		await this.room.setE2EEEnabled(!recording);
		this.recordingMode = recording;
	}

	private bindRoom(room: Room, livekit: typeof import('livekit-client')): void {
		const sync = () => this.syncLocalState(room);
		const refresh = () => void this.refreshDevices();
		const recoverLocalTrack = (source: string) => {
			if (source === livekit.Track.Source.Camera && this.cameraRequested)
				void this.recoverCamera(room);
			if (source === livekit.Track.Source.Microphone && this.microphoneRequested)
				void this.recoverMicrophone(room);
		};
		room
			.on(livekit.RoomEvent.ConnectionStateChanged, (state) => {
				this.connectionState = state;
			})
			.on(livekit.RoomEvent.Reconnecting, () => {
				this.connectionState = 'reconnecting';
			})
			.on(livekit.RoomEvent.Reconnected, () => {
				this.connectionState = 'connected';
				sync();
				refresh();
			})
			.on(livekit.RoomEvent.ParticipantConnected, () => (this.revision += 1))
			.on(livekit.RoomEvent.ParticipantDisconnected, (participant) => {
				this.removeAudio(participant.identity);
				const qualities = { ...this.connectionQualities };
				delete qualities[participant.identity];
				this.connectionQualities = qualities;
				this.revision += 1;
			})
			.on(livekit.RoomEvent.ConnectionQualityChanged, (quality, participant) => {
				this.setConnectionQuality(participant.identity, quality);
			})
			.on(livekit.RoomEvent.TrackPublished, () => (this.revision += 1))
			.on(livekit.RoomEvent.TrackSubscribed, (track, _publication, participant) => {
				if (track.kind === livekit.Track.Kind.Audio) this.attachAudio(participant.identity, track);
				track.mediaStreamTrack.addEventListener('ended', () => (this.revision += 1), {
					once: true
				});
				this.revision += 1;
			})
			.on(livekit.RoomEvent.TrackUnsubscribed, (track, _publication, participant) => {
				track.detach().forEach((element) => element.remove());
				this.removeAudio(participant.identity);
				this.revision += 1;
			})
			.on(livekit.RoomEvent.TrackMuted, (publication, participant) => {
				sync();
				if (participant.identity === room.localParticipant.identity)
					recoverLocalTrack(publication.source);
			})
			.on(livekit.RoomEvent.TrackUnmuted, sync)
			.on(livekit.RoomEvent.LocalTrackPublished, (publication) => {
				sync();
				publication.track?.on(livekit.TrackEvent.Ended, () => {
					sync();
					recoverLocalTrack(publication.source);
				});
			})
			.on(livekit.RoomEvent.LocalTrackUnpublished, sync)
			.on(livekit.RoomEvent.MediaDevicesChanged, refresh)
			.on(livekit.RoomEvent.ActiveDeviceChanged, refresh)
			.on(livekit.RoomEvent.AudioPlaybackStatusChanged, () => {
				this.audioPlaybackBlocked = !room.canPlaybackAudio;
			});
		room.on(livekit.RoomEvent.ActiveSpeakersChanged, (speakers) => {
			this.speakingIds = speakers.map((participant) => participant.identity);
		});
	}

	private setConnectionQuality(userId: string, quality: ConnectionQuality): void {
		this.connectionQualities = {
			...this.connectionQualities,
			[userId]: quality as MediaConnectionQuality
		};
	}

	/**
	 * Мобильные браузеры иногда завершают только что выданный трек при смене
	 * камеры, возврате из фона или исчезновении устройства. LiveKit пытается
	 * перезапустить его сам, но при кратком конфликте оставляет публикацию
	 * заглушённой. Повторяем захват после освобождения устройства, только если
	 * человек всё ещё хочет передавать видео.
	 */
	private async recoverCamera(room: Room): Promise<void> {
		if (this.cameraRecovery || this.room !== room || !this.cameraRequested) return;
		this.cameraRecovery = (async () => {
			this.cameraError = 'Камера отключилась. Пытаемся подключить её снова…';
			for (const delay of [350, 700]) {
				await new Promise((resolve) => setTimeout(resolve, delay));
				if (this.room !== room || !this.cameraRequested) return;
				try {
					await room.localParticipant.setCameraEnabled(true, {
						facingMode: this.preferredCameraFacingMode
					});
					this.syncLocalState(room);
					if (this.cameraEnabled) {
						this.cameraError = null;
						await this.refreshDevices();
						return;
					}
				} catch (problem) {
					this.cameraError = mediaError('camera', problem);
				}
			}
			if (this.room === room && this.cameraRequested && !this.cameraEnabled)
				this.cameraError = 'Камера отключилась. Проверьте устройство и включите её снова.';
		})().finally(() => {
			this.cameraRecovery = null;
		});
		await this.cameraRecovery;
	}

	private async recoverMicrophone(room: Room): Promise<void> {
		if (this.microphoneRecovery || this.room !== room || !this.microphoneRequested) return;
		this.microphoneRecovery = (async () => {
			this.microphoneError = 'Микрофон отключился. Пытаемся подключить его снова…';
			for (const delay of [350, 700]) {
				await new Promise((resolve) => setTimeout(resolve, delay));
				if (this.room !== room || !this.microphoneRequested) return;
				try {
					await room.localParticipant.setMicrophoneEnabled(true);
					this.syncLocalState(room);
					if (this.microphoneEnabled) {
						this.microphoneError = null;
						await this.refreshDevices();
						return;
					}
				} catch (problem) {
					this.microphoneError = mediaError('microphone', problem);
				}
			}
			if (this.room === room && this.microphoneRequested && !this.microphoneEnabled)
				this.microphoneError = 'Микрофон отключился. Проверьте устройство и включите его снова.';
		})().finally(() => {
			this.microphoneRecovery = null;
		});
		await this.microphoneRecovery;
	}

	private watchLevels(room: Room): void {
		if (this.levelTimer) clearInterval(this.levelTimer);
		this.levelTimer = setInterval(() => {
			if (this.room !== room) return;
			const next: Record<string, number> = {
				[room.localParticipant.identity]: room.localParticipant.audioLevel ?? 0
			};
			for (const participant of room.remoteParticipants.values())
				next[participant.identity] = participant.audioLevel ?? 0;
			this.levels = next;
		}, 100);
	}

	private syncLocalState(room: Room): void {
		if (this.room !== room) return;
		this.microphoneEnabled = room.localParticipant.isMicrophoneEnabled;
		this.cameraEnabled = room.localParticipant.isCameraEnabled;
		this.screenShareEnabled = room.localParticipant.isScreenShareEnabled;
		this.revision += 1;
	}

	private attachAudio(participantId: string, track: RemoteTrack): void {
		const element = track.attach();
		element.autoplay = true;
		element.dataset.andresotCallAudio = participantId;
		element.style.display = 'none';
		document.body.append(element);
		void element.play().then(
			() => (this.audioPlaybackBlocked = false),
			() => (this.audioPlaybackBlocked = true)
		);
		const list = this.audioElements.get(participantId) ?? [];
		list.push(element);
		this.audioElements.set(participantId, list);
	}

	private removeAudio(participantId: string): void {
		for (const element of this.audioElements.get(participantId) ?? []) element.remove();
		this.audioElements.delete(participantId);
	}

	async startAudio(): Promise<void> {
		if (!this.room) return;
		try {
			await this.room.startAudio();
			this.audioPlaybackBlocked = !this.room.canPlaybackAudio;
		} catch {
			this.audioPlaybackBlocked = true;
		}
	}

	attachVideo(
		participantId: string,
		element: HTMLVideoElement,
		source: CallVideoSource = 'camera'
	): () => void {
		void this.revision;
		const publication = this.videoPublication(participantId, source);
		const track = publication?.videoTrack;
		if (track && !publication.isMuted) {
			track.attach(element);
			element.autoplay = true;
			element.playsInline = true;
			if (participantId === this.room?.localParticipant.identity) element.muted = true;
		}
		return () => track?.detach(element);
	}

	hasVideo(participantId: string, source: CallVideoSource = 'camera'): boolean {
		void this.revision;
		const publication = this.videoPublication(participantId, source);
		return Boolean(
			publication?.videoTrack &&
			publication.videoTrack.mediaStreamTrack.readyState === 'live' &&
			!publication.isMuted
		);
	}

	/*
	 * У участника бывает две картинки: камера и экран. Раньше бралась только
	 * камера — экран уходил в комнату, сервер отмечал «показывает экран», а
	 * собеседники его не видели: показать было нечем.
	 */
	private videoPublication(
		participantId: string,
		source: CallVideoSource = 'camera'
	): VideoPublication | undefined {
		if (!this.room) return undefined;
		const participant =
			participantId === this.room.localParticipant.identity
				? this.room.localParticipant
				: this.room.remoteParticipants.get(participantId);
		const wanted = source === 'screen' ? 'screen_share' : 'camera';
		return [...(participant?.videoTrackPublications.values() ?? [])].find(
			(publication) => publication.source === wanted
		) as VideoPublication | undefined;
	}

	async setMicrophone(enabled: boolean): Promise<boolean> {
		if (!this.room) return false;
		this.microphoneRequested = enabled;
		try {
			await this.room.localParticipant.setMicrophoneEnabled(enabled);
			this.microphoneError = null;
		} catch (problem) {
			this.microphoneError = mediaError('microphone', problem);
			throw problem;
		} finally {
			this.syncLocalState(this.room);
		}
		if (enabled && !this.microphoneEnabled && !this.microphoneError)
			this.microphoneError = 'Микрофон не запущен. Проверьте устройство и разрешения браузера.';
		return this.microphoneEnabled;
	}

	async setCamera(enabled: boolean): Promise<boolean> {
		if (!this.room) return false;
		this.cameraRequested = enabled;
		try {
			await this.room.localParticipant.setCameraEnabled(
				enabled,
				enabled ? { facingMode: this.preferredCameraFacingMode } : undefined
			);
			this.cameraError = null;
		} catch (problem) {
			this.cameraError = mediaError('camera', problem);
			throw problem;
		} finally {
			this.syncLocalState(this.room);
		}
		if (enabled && !this.cameraEnabled && !this.cameraError)
			this.cameraError = 'Камера не запущена. Проверьте устройство и разрешения браузера.';
		return this.cameraEnabled;
	}

	async flipCamera(): Promise<boolean> {
		if (!this.room || this.deviceSwitchingKind) return false;
		const previous = this.preferredCameraFacingMode;
		const target: CameraFacingMode = previous === 'user' ? 'environment' : 'user';
		this.preferredCameraFacingMode = target;
		if (!this.cameraEnabled) return true;
		this.deviceSwitchingKind = 'videoinput';
		try {
			const track = this.videoPublication(this.room.localParticipant.identity)?.videoTrack;
			if (track && 'restartTrack' in track) {
				await track.restartTrack({ facingMode: target });
			} else {
				const fallback = this.devices.find(
					(device) =>
						device.kind === 'videoinput' &&
						(target === 'environment'
							? /back|rear|environment|зад/i.test(device.label)
							: /front|face|user|перед/i.test(device.label))
				);
				if (
					!fallback ||
					!(await this.room.switchActiveDevice('videoinput', fallback.deviceId, true))
				)
					throw new Error('Нужная камера не найдена');
			}
			this.cameraError = null;
			await this.refreshDevices();
			return true;
		} catch (problem) {
			this.preferredCameraFacingMode = previous;
			this.cameraError = mediaError('camera', problem);
			return false;
		} finally {
			this.deviceSwitchingKind = null;
		}
	}

	async setScreenShare(enabled: boolean): Promise<boolean> {
		if (!this.room) return false;
		if (enabled && !this.screenShareSupported)
			throw new Error('Этот браузер не поддерживает демонстрацию экрана');
		await this.room.localParticipant.setScreenShareEnabled(enabled);
		this.syncLocalState(this.room);
		return this.screenShareEnabled;
	}

	async refreshDevices(): Promise<void> {
		if (!navigator.mediaDevices?.enumerateDevices) return;
		try {
			this.devices = await navigator.mediaDevices.enumerateDevices();
			this.activeAudioInputDeviceId = this.room?.getActiveDevice('audioinput') ?? null;
			this.activeVideoDeviceId = this.room?.getActiveDevice('videoinput') ?? null;
			this.activeAudioOutputDeviceId = this.room?.getActiveDevice('audiooutput') ?? null;
		} catch {
			/* published tracks can keep working when enumeration is blocked */
		}
	}

	async switchDevice(kind: MediaDeviceKind, deviceId: string): Promise<boolean> {
		if (!this.room || !deviceId || this.deviceSwitchingKind) return false;
		if (kind === 'audiooutput' && !this.speakerSelectionSupported) return false;
		this.deviceSwitchingKind = kind;
		try {
			if (!(await this.room.switchActiveDevice(kind, deviceId, true)))
				throw new Error('LiveKit не подтвердил переключение устройства');
			if (kind === 'videoinput') {
				this.cameraError = null;
				const label = this.devices.find((device) => device.deviceId === deviceId)?.label ?? '';
				if (/back|rear|environment|зад/i.test(label))
					this.preferredCameraFacingMode = 'environment';
				else if (/front|face|user|перед/i.test(label)) this.preferredCameraFacingMode = 'user';
			}
			if (kind === 'audioinput') this.microphoneError = null;
			this.error = null;
			await this.refreshDevices();
			return true;
		} catch (problem) {
			if (kind === 'videoinput') this.cameraError = mediaError('camera', problem);
			else if (kind === 'audioinput') this.microphoneError = mediaError('microphone', problem);
			else this.error = 'Не удалось переключить динамик.';
			return false;
		} finally {
			this.deviceSwitchingKind = null;
		}
	}
}

export const callMedia = new CallMediaState();
